/**
 * The tenant-level navigation, as a sidebar rather than a strip of cards.
 *
 * The cards sat in the content column, so they scrolled away and competed with
 * each page's own heading for the top of the screen. A sidebar stays put, and
 * the destinations stop being page content.
 *
 * @module components/shell/WorkspaceSidebar
 */

import React from "react";

import { BrandWordmark } from "@/components/shared/BrandWordmark";
import { Badge, Sidebar, SidebarNavItem, SidebarSection } from "@/components/ui";
import { ADMIN_AREA } from "@/router/admin/admin-area";
import type { WorkspacePermissions } from "@/router/workspace-context";

import { SidebarFooter } from "./SidebarFooter";

export type WorkspaceSidebarPermissions = Pick<
  WorkspacePermissions,
  | "isPlatformAdmin"
  | "canAccessWorkspace"
  | "canManageProgrammeTeam"
  | "canManageTenant"
  | "canViewReports"
>;

export interface WorkspaceSidebarProps extends WorkspaceSidebarPermissions {
  /** Named in the brand header, so it is always clear whose data is open. */
  tenantName: string;
  /** Shown in the footer, for support. */
  version?: string;
}

interface Destination {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  hidden?: boolean;
  testId: string;
}

/** One renderer for both sections, so they cannot drift apart. */
function navItem(item: Destination): React.ReactElement {
  return (
    <SidebarNavItem
      key={item.to}
      to={item.to}
      label={item.label}
      icon={item.icon}
      end={item.end}
      data-testid={item.testId}
    />
  );
}

/**
 * @example
 * ```tsx
 * <WorkspaceSidebar tenantName={session.tenant.name} {...permissions} />
 * ```
 */
export function WorkspaceSidebar({
  tenantName,
  version = undefined,
  isPlatformAdmin,
  canAccessWorkspace,
  canManageProgrammeTeam,
  canManageTenant,
  canViewReports,
}: WorkspaceSidebarProps): React.ReactElement {
  const primary: Destination[] = [
    {
      to: "/",
      label: "Overview",
      icon: "squares-four",
      end: true,
      testId: "nav-workspace-overview",
    },
    {
      to: "/programmes",
      label: "Programmes",
      icon: "graduation-cap",
      hidden: !canAccessWorkspace,
      testId: "nav-workspace-programmes",
    },
    {
      to: "/portfolio",
      label: "Portfolio",
      icon: "archive",
      hidden: !canAccessWorkspace,
      testId: "nav-workspace-portfolio",
    },
    {
      to: "/programme-teams",
      label: "Programme teams",
      icon: "users-three",
      hidden: !canManageProgrammeTeam,
      testId: "nav-workspace-programme-teams",
    },
    {
      to: "/reports",
      label: "Reports",
      icon: "chart-bar",
      hidden: !canViewReports,
      testId: "nav-workspace-reports",
    },
  ];

  const administration: Destination[] = [
    {
      to: "/admin",
      label: isPlatformAdmin ? ADMIN_AREA.platform : ADMIN_AREA.client,
      icon: "gear",
      hidden: !canManageTenant,
      testId: "nav-workspace-admin",
    },
  ];

  const visible = (items: Destination[]): Destination[] => items.filter((item) => !item.hidden);

  return (
    <Sidebar
      data-testid="workspace-sidebar"
      header={
        <div className="flex flex-col gap-2">
          <BrandWordmark size="lg" className="leading-none" />
          {/* The whole name, wrapped: a name cut at the sidebar's edge says
              different things depending on how long the tenant's name is. */}
          <Badge
            tone="ink"
            size="sm"
            className="h-auto max-w-full py-1 text-left whitespace-normal [overflow-wrap:anywhere]"
            data-testid="signed-in-tenant"
          >
            {tenantName}
          </Badge>
        </div>
      }
      footer={<SidebarFooter version={version} />}
    >
      <SidebarSection>{visible(primary).map(navItem)}</SidebarSection>
      {visible(administration).length > 0 && (
        <SidebarSection label="Administration">
          {visible(administration).map(navItem)}
        </SidebarSection>
      )}
    </Sidebar>
  );
}
